import { useEffect, useState } from "react";

import {
  useApproveProfile,
  useProfile,
  useProfileImpact,
  useResolveProfile,
  useSaveProfile,
  useSchemes,
} from "@/api/queries";
import { TAXONOMY } from "@/lib/taxonomy";
import { toContent, toSelections, type Selections } from "@/lib/profile";
import { Button } from "@/components/Button";
import { DefinitionList } from "@/components/DefinitionList";
import { ErrorState } from "@/components/ErrorState";
import { Modal } from "@/components/Modal";
import { Panel, SectionTitle } from "@/components/Panel";
import { RemoteState } from "@/components/RemoteState";
import { StatusLabel } from "@/components/StatusLabel";
import { Tag } from "@/components/Tag";
import { useUiStore } from "@/store/ui";

interface ConflictLike {
  dimensions?: string[];
  rule?: string;
  message?: string;
  suggestion?: string;
}

/**
 * 项目分类向导（specs/07 §4 · §12.1 / PR-101）。
 *
 * 交互契约：
 * 1. 六维**独立选择**，未选满不得解析；
 * 2. 每次改动都走 `/profile/resolve`，冲突与影响预览即时可见；
 * 3. G-1 锁定后 Profile 不可再改，需要改则必须新版本并重新评审（ADR-004）。
 *
 * @dataSource remote
 */
export function ClassifyView() {
  const projectId = useUiStore((state) => state.projectId);
  const pushToast = useUiStore((state) => state.pushToast);

  const profile = useProfile(projectId);
  const schemes = useSchemes(projectId);
  const resolve = useResolveProfile(projectId);
  const save = useSaveProfile(projectId);
  const approve = useApproveProfile(projectId);
  /** 变更影响预览：当前 Profile ↔ 候选选择 的差异（specs/14 §4.3）。 */
  const impact = useProfileImpact(projectId);

  const [selections, setSelections] = useState<Selections>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [impactOpen, setImpactOpen] = useState(false);

  // 已锁定的 Profile 直接回填，避免“看着是空的其实已锁定”
  useEffect(() => {
    if (profile.data && Object.keys(selections).length === 0) {
      setSelections(toSelections(profile.data.content));
    }
  }, [profile.data, selections]);

  const locked = profile.data?.status === "approved";
  const filledCount = TAXONOMY.filter((dim) => selections[dim.id]).length;
  const complete = filledCount === TAXONOMY.length;

  const resolveResult = resolve.data;
  const conflicts = (resolveResult?.conflicts ?? []) as ConflictLike[];
  const blocking = Boolean(resolveResult?.blocking);

  // 未解析前回显已锁定 Profile 的适配器链与生产参数
  const adapters = resolveResult?.adapters ?? profile.data?.adapters ?? [];
  const resolvedParams = resolveResult?.resolved ?? profile.data?.resolved ?? {};

  function select(dimensionId: string, value: string) {
    if (locked) return;
    const next = { ...selections, [dimensionId]: value };
    setSelections(next);
    if (Object.keys(next).length === TAXONOMY.length) {
      resolve.mutate(toContent(next, profile.data?.content ?? {}));
    }
  }

  return (
    <div className="page-scroll">
      <div className="page-width">
        {/*
          Profile / 候选方案任一失败都必须整块呈现错误态（AC-1401 / RULES §11.2）。
          此前失败时向导照旧渲染「0 / 6 已选择 · 未锁定」——那会把一个**已经 G-1 锁定**的
          项目显示成"尚未分类"，并邀请用户重做六维分类（达标的 Profile 会被当成没做过）。
          ⚠️ `impact` 刻意留在边界**之外**：它是弹窗内的一次性预览，已有自己的错误态。
        */}
        <RemoteState queries={[profile, schemes]} title="分类数据加载失败">
          <header className="page-heading">
            <div>
              <span className="eyebrow">六维分类 · 适配器解析顺序</span>
              <h2>先说清这是什么类型的作品，再开始写它</h2>
              <p>
                六维彼此独立，逐项选择后系统会解析出适配器链与规则集。分类一旦通过 G-1
                锁定即成为项目的方法论基线，后续改动必须新版本并重新评审。
              </p>
            </div>
            <div className="meta-row">
              <Tag tone={complete ? "green" : "gold"}>
                {filledCount} / {TAXONOMY.length} 已选择
              </Tag>
              {locked ? (
                <Tag tone="blue">G-1 已锁定 v{profile.data?.version ?? 1}</Tag>
              ) : (
                <Tag tone="red">未锁定</Tag>
              )}
            </div>
          </header>

          <div className="wizard-layout">
            <div className="wizard-steps">
              {TAXONOMY.map((dimension, index) => {
                const active = index === currentIndex;
                const chosen = selections[dimension.id];
                return (
                  <div
                    className={`wizard-step-card ${active ? "is-active" : ""}`}
                    key={dimension.id}
                  >
                    <div className="section-title">
                      <div>
                        <h3>
                          {String(dimension.index).padStart(2, "0")} · {dimension.title}
                        </h3>
                        <p>{dimension.question}</p>
                      </div>
                      <div className="toolbar-right">
                        {chosen ? <Tag tone="green">已选</Tag> : <Tag tone="gold">待选</Tag>}
                        {active ? null : (
                          <Button
                            size="small"
                            variant="quiet"
                            onClick={() => setCurrentIndex(index)}
                          >
                            展开
                          </Button>
                        )}
                      </div>
                    </div>

                    {active ? (
                      <>
                        <p className="inline-note">{dimension.help}</p>
                        <div className="wizard-option-grid">
                          {dimension.options.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              className={`wizard-option ${chosen === option.value ? "is-selected" : ""}`}
                              aria-pressed={chosen === option.value}
                              disabled={locked}
                              onClick={() => select(dimension.id, option.value)}
                            >
                              <strong>{option.label}</strong>
                              <small>{option.hint}</small>
                            </button>
                          ))}
                        </div>
                        <div className="toolbar-right" style={{ marginTop: 14 }}>
                          <Button
                            size="small"
                            variant="quiet"
                            disabled={index === 0}
                            onClick={() => setCurrentIndex(index - 1)}
                          >
                            上一维
                          </Button>
                          <Button
                            size="small"
                            variant="quiet"
                            disabled={index === TAXONOMY.length - 1}
                            onClick={() => setCurrentIndex(index + 1)}
                          >
                            下一维
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p className="inline-note">
                        {chosen
                          ? `当前选择：${
                              dimension.options.find((option) => option.value === chosen)?.label ??
                              chosen
                            }`
                          : "尚未选择"}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <aside>
              <Panel
                title="解析结果"
                description="顺序：INVARIANT → 产品形态 → 交互 → 平台 → 类型 → 题材"
              >
                <div className="meta-row">
                  <StatusLabel tone={blocking ? "danger" : complete ? "success" : "warning"}>
                    {blocking ? "存在阻塞冲突" : complete ? "可锁定" : "待选满六维"}
                  </StatusLabel>
                  <Tag>方法包 v{profile.data?.version ?? 1}</Tag>
                </div>

                {conflicts.length > 0 ? (
                  <div className="conflict-banner">
                    <span className="mini-mark" aria-hidden="true">
                      !
                    </span>
                    <div>
                      {conflicts.map((conflict, index) => (
                        <p key={index}>
                          {conflict.rule ? `${conflict.rule} · ` : ""}
                          {conflict.message}
                          {conflict.suggestion ? <br /> : null}
                          {conflict.suggestion ? (
                            <strong>建议：{conflict.suggestion}</strong>
                          ) : null}
                        </p>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="section-title" style={{ marginTop: 16 }}>
                  <div>
                    <h3>适配器链</h3>
                    <p>每层只能覆盖 HEURISTIC 字段；INVARIANT 层不可覆盖</p>
                  </div>
                </div>
                {adapters.length === 0 ? (
                  <p className="inline-note">选满六维后显示解析出的适配器链。</p>
                ) : (
                  <ul className="activity-list">
                    {adapters.map((adapter) => (
                      <li className="activity-item" key={`${adapter.kind}-${adapter.id}`}>
                        <span className="mini-mark">◇</span>
                        <div>
                          <p>
                            {adapter.label} · <small>{adapter.kind}</small>
                          </p>
                          <small>{adapter.summary}</small>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="section-title" style={{ marginTop: 16 }}>
                  <div>
                    <h3>解析出的生产参数</h3>
                    <p>直接决定选择密度、校验规则与交付物清单</p>
                  </div>
                </div>
                {Object.keys(resolvedParams).length === 0 ? (
                  <p className="inline-note">选满六维后显示。</p>
                ) : (
                  <dl className="definition-list">
                    {Object.entries(resolvedParams).map(([key, value]) => (
                      <div key={key} style={{ display: "contents" }}>
                        <dt>{RESOLVED_LABEL[key] ?? key}</dt>
                        <dd>{formatResolved(value)}</dd>
                      </div>
                    ))}
                  </dl>
                )}

                <div className="toolbar-right" style={{ marginTop: 16 }}>
                  <Button
                    size="small"
                    variant="quiet"
                    id="profile-impact"
                    disabled={!complete}
                    onClick={() => {
                      setImpactOpen(true);
                      impact.mutate({
                        current: profile.data?.content ?? {},
                        candidate: toContent(selections, profile.data?.content ?? {}),
                      });
                    }}
                  >
                    变更影响
                  </Button>
                  <Button
                    size="small"
                    variant="quiet"
                    disabled={!complete || locked || resolve.isPending}
                    onClick={() =>
                      resolve.mutate(toContent(selections, profile.data?.content ?? {}))
                    }
                  >
                    重新解析
                  </Button>
                  <Button
                    size="small"
                    disabled={!complete || blocking || locked || save.isPending}
                    onClick={() =>
                      save.mutate(toContent(selections, profile.data?.content ?? {}), {
                        onSuccess: () =>
                          pushToast({
                            title: "分类已保存",
                            detail: "保存后仍需通过 G-1 锁定才生效",
                            mark: "✓",
                          }),
                      })
                    }
                  >
                    保存分类
                  </Button>
                </div>
              </Panel>

              <Panel
                title="影响预览"
                description="候选方案改写哪些产物、增加多少场景与额度"
                className="community-funnel"
                bodyClassName="method-list"
              >
                {(schemes.data ?? []).map((scheme) => (
                  <article className="method-card" key={scheme.id}>
                    <div className="meta-row">
                      <Tag tone={scheme.conflicts.length ? "red" : "gold"}>{scheme.name}</Tag>
                      <Tag>{scheme.resolution_status}</Tag>
                    </div>
                    <p style={{ marginTop: 8 }}>{scheme.difference}</p>
                    <p className="inline-note">后果：{scheme.consequence}</p>
                    <div className="method-meta">
                      <span>
                        改写 {String(scheme.impact.rewritten_artifacts ?? 0)} 个产物 · 新增{" "}
                        {String(scheme.impact.added_scenes ?? 0)} 场景 · 额度 +
                        {String(scheme.impact.cost_delta ?? 0)}
                      </span>
                      {scheme.conflicts.length ? (
                        <StatusLabel tone="danger">冲突</StatusLabel>
                      ) : null}
                    </div>
                  </article>
                ))}
              </Panel>
            </aside>
          </div>

          <SectionTitle
            title="G-1 锁定"
            description="锁定后本项目的规则集、适配器链与检查阈值不再自动升级"
            actions={
              <Button
                variant="primary"
                disabled={!complete || blocking || locked || approve.isPending}
                onClick={() =>
                  approve.mutate("六维分类确认，进入 Brief 阶段", {
                    onSuccess: () =>
                      pushToast({
                        title: "G-1 已锁定",
                        detail: "后续方法包升级需显式迁移，禁止自动跨过 S6（ADR-004）",
                        mark: "✓",
                      }),
                  })
                }
              >
                {locked ? "已锁定" : "锁定 G-1"}
              </Button>
            }
          />
        </RemoteState>
      </div>

      <Modal
        open={impactOpen}
        onClose={() => setImpactOpen(false)}
        className="modal-wide"
        eyebrow="变更影响预览"
        title="改这几维会波及什么"
        footer={
          <Button variant="quiet" onClick={() => setImpactOpen(false)}>
            关闭
          </Button>
        }
      >
        {impact.isPending ? (
          <p className="inline-note">计算中…</p>
        ) : impact.isError ? (
          <ErrorState title="影响预览失败" error={impact.error} />
        ) : impact.data ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div className="meta-row">
              <StatusLabel
                tone={
                  impact.data.full_rebuild
                    ? "danger"
                    : impact.data.changed_fields.length > 0
                      ? "warning"
                      : "success"
                }
              >
                {impact.data.full_rebuild
                  ? "必须整体重建"
                  : impact.data.changed_fields.length > 0
                    ? "可局部重跑"
                    : "无变化"}
              </StatusLabel>
              <Tag>受影响阶段 {impact.data.stages.length}</Tag>
              <Tag>受影响产物类型 {impact.data.artifact_types.length}</Tag>
            </div>

            <DefinitionList
              entries={[
                { term: "变更字段", value: impact.data.changed_fields.join(" · ") || "无" },
                { term: "受影响阶段", value: impact.data.stages.join(" · ") || "无" },
                {
                  term: "受影响产物类型",
                  value: impact.data.artifact_types.join(" · ") || "无",
                },
                { term: "恢复建议", value: impact.data.recommendation || "—" },
              ]}
            />

            {impact.data.notes.length > 0 ? (
              <ul className="issue-list">
                {impact.data.notes.map((note, index) => (
                  <li className="issue-item" key={index}>
                    <span className="mini-mark">◇</span>
                    <div>
                      <p>{note}</p>
                    </div>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : (
          <p className="inline-note">点击「变更影响」后显示。</p>
        )}
      </Modal>
    </div>
  );
}

const RESOLVED_LABEL: Record<string, string> = {
  path_model: "路径模型",
  choice_density: "选择密度",
  failure_policy: "失败策略",
  replay_policy: "重玩策略",
  state_visibility: "状态可见性",
  deliverables: "交付物",
  validators: "启用校验规则",
};

function formatResolved(value: unknown): string {
  if (Array.isArray(value)) return value.join(" · ");
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (record.min !== undefined && record.max !== undefined) {
      return `每集 ${String(record.min)}-${String(record.max)} 次${
        record.placement ? ` · ${String(record.placement)}` : ""
      }`;
    }
    return JSON.stringify(value);
  }
  return String(value);
}
